// @flow
import { useEffect, useLayoutEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
// Types
import type { Node } from 'react';
import type { RouterHistory } from 'react-router-dom';

const RESTORE_TIMEOUT_MS = 5000;
const USER_SCROLL_EVENTS = ['wheel', 'touchstart', 'keydown', 'mousedown'];

const scrollPositions = new Map();

export const isRestoringScrollPosition = (history: RouterHistory) =>
  history.action === 'POP' && scrollPositions.has(history.location.key);

const restoreScrollPosition = (top: number) => {
  const deadline = Date.now() + RESTORE_TIMEOUT_MS;
  let frameId;

  const stop = () => {
    cancelAnimationFrame(frameId);
    USER_SCROLL_EVENTS.forEach(event =>
      window.removeEventListener(event, stop),
    );
  };

  const restore = () => {
    window.scrollTo(0, top);
    if (Date.now() < deadline) {
      frameId = requestAnimationFrame(restore);
    } else {
      stop();
    }
  };

  USER_SCROLL_EVENTS.forEach(event =>
    window.addEventListener(event, stop, { passive: true }),
  );
  restore();

  return stop;
};

type Props = {
  children: Node,
};

const ScrollRestoration = ({ children }: Props) => {
  const history = useHistory();
  const location = useLocation();

  useEffect(() => {
    window.history.scrollRestoration = 'manual';

    let currentKey = history.location.key;
    return history.listen(nextLocation => {
      scrollPositions.set(currentKey, window.scrollY);
      currentKey = nextLocation.key;
    });
  }, [history]);

  useLayoutEffect(() => {
    if (isRestoringScrollPosition(history)) {
      return restoreScrollPosition(scrollPositions.get(location.key));
    }
    window.scrollTo(0, 0);
  }, [history, location]);

  return children;
};

export default ScrollRestoration;
